<script setup lang="ts">
import {
  Button,
  Form,
  FormItem,
  Input,
  Select,
  Textarea,
} from 'ant-design-vue';
import type { CombinedError } from '@urql/vue';

import { GenericCompleteDraftEnum, FileAiPromptTypeEnum } from '@/graphql/generated/graphql';
import AlertError from '@/ui/NoDataUrql/AlertError.vue';
import { required } from '~/util/rules';
import enumToArray from '~/util/enumToArray';

export interface FileAiPromptFormValues {
  type: FileAiPromptTypeEnum;
  version: string;
  prompt: string;
  schema: string;
  status: GenericCompleteDraftEnum;
}
const props = defineProps<{
  initialValues?: Partial<FileAiPromptFormValues>;
  fetching?: boolean;
  error?: CombinedError | null;
  submitLabel?: string;
}>();

const emit = defineEmits<{
  submit: [values: FileAiPromptFormValues];
  cancel: [];
}>();

const formState = reactive<Partial<FileAiPromptFormValues>>({ ...props.initialValues });

const rules = {
  type: required('Type is required.'),
  version: required('Version is required.'),
  prompt: required('Prompt is required.'),
  schema: required('Schema is required.'),
  status: required('Status is required.'),
}

const statusOptions: { label: string; value: GenericCompleteDraftEnum }[] = [
  { label: 'Draft', value: GenericCompleteDraftEnum.Draft },
  { label: 'Completed', value: GenericCompleteDraftEnum.Completed },
];

const onFinish = (values: FileAiPromptFormValues) => {
  emit('submit', values);
};

const onCancel = () => {
  emit('cancel');
};
</script>

<template>
  <Form
    layout="vertical"
    :model="formState"
    :rules="rules"
    @finish="onFinish"
  >
    <FormItem label="Type" name="type">
      <Select
        v-model:value="formState.type"
        :options="enumToArray(FileAiPromptTypeEnum)"
        placeholder="Select prompt type"
        :disabled="fetching"
      />
    </FormItem>
    <FormItem label="Version" name="version">
      <Input
        v-model:value="formState.version"
        placeholder="e.g. 1.0"
        :disabled="fetching"
      />
    </FormItem>
    <FormItem label="Status" name="status">
      <Select
        v-model:value="formState.status"
        :options="statusOptions"
        :disabled="fetching"
      />
    </FormItem>
    <FormItem label="Prompt" name="prompt">
      <Textarea
        v-model:value="formState.prompt"
        :rows="10"
        placeholder="Prompt text sent to the model"
        :disabled="fetching"
      />
    </FormItem>
    <FormItem label="Schema (JSON)" name="schema">
      <Textarea
        v-model:value="formState.schema"
        :rows="12"
        class="font-mono text-sm"
        placeholder='{ "type": "object", ... }'
        :disabled="fetching"
      />
    </FormItem>
    <AlertError
      v-if="error"
      :error="error"
      class="mb-4"
    />
    <FormItem>
      <div class="flex justify-end gap-2">
        <Button html-type="button" :disabled="fetching" @click="onCancel">
          Cancel
        </Button>
        <Button
          type="primary"
          html-type="submit"
          :loading="fetching"
        >
          {{ submitLabel ?? 'Save' }}
        </Button>
      </div>
    </FormItem>
  </Form>
</template>
